import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCheck, FiX, FiTrendingUp, FiDollarSign, FiZap, FiShield, FiArrowRight } from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import { useSEO } from '../hooks/useSEO';
import './APM.css';

const LogCrestVsNewRelic = () => {
  useSEO({
    title: 'LogCrest vs New Relic - APM & Observability Comparison | LogCrest',
    description: 'Compare LogCrest vs New Relic. AI-powered observability platform with comprehensive APM, log management, and infrastructure monitoring. Better pricing and features.',
    keywords: 'LogCrest vs New Relic, New Relic alternative, newrelic competitor, APM comparison, observability platform comparison, application performance monitoring alternative'
  });

  useEffect(() => {
    trackPageView('/logcrest-vs-newrelic', {
      page_title: 'LogCrest vs New Relic',
      page_category: 'comparison'
    });
  }, []);

  const comparison = [
    { feature: 'AI-Powered APM', logcrest: true, newrelic: false },
    { feature: 'Unified Observability', logcrest: true, newrelic: false },
    { feature: 'Better Pricing', logcrest: true, newrelic: false },
    { feature: 'Application Performance Monitoring', logcrest: true, newrelic: true },
    { feature: 'Infrastructure Monitoring', logcrest: true, newrelic: true },
    { feature: 'Log Management', logcrest: true, newrelic: true },
    { feature: 'Real-Time Analytics', logcrest: true, newrelic: true },
    { feature: 'Cloud-Native', logcrest: true, newrelic: true }
  ];

  return (
    <div className="agent-sre-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>LogCrest vs New Relic</h1>
            <p className="hero-subtitle">
              Compare LogCrest with New Relic. AI-powered observability platform with comprehensive APM and better pricing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="features" style={{ padding: '6rem 0' }}>
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Feature Comparison</h2>
            <p>See how LogCrest compares to New Relic</p>
          </motion.div>
          
          <div style={{ 
            background: 'var(--background-alt)', 
            borderRadius: '16px', 
            overflow: 'hidden', 
            border: '1px solid var(--border-color)',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '2fr 1fr 1fr', 
              background: 'var(--gradient-primary)', 
              color: 'var(--text-white)', 
              padding: '1.5rem',
              fontWeight: '700',
              fontSize: '1.125rem'
            }}>
              <div>Feature</div>
              <div style={{ textAlign: 'center' }}>LogCrest</div>
              <div style={{ textAlign: 'center' }}>New Relic</div>
            </div>
            {comparison.map((item, index) => (
              <motion.div 
                key={index}
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '2fr 1fr 1fr', 
                  padding: '1.5rem', 
                  borderBottom: index < comparison.length - 1 ? '1px solid var(--border-color)' : 'none',
                  background: index % 2 === 0 ? 'var(--background-alt)' : 'var(--background)',
                  transition: 'background 0.2s'
                }}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background-light)'}
                onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'var(--background-alt)' : 'var(--background)'}
              >
                <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{item.feature}</div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {item.logcrest ? <FiCheck style={{ color: 'var(--success-color)', fontSize: '1.5rem' }} /> : <FiX style={{ color: 'var(--danger-color)', fontSize: '1.5rem' }} />}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {item.newrelic ? <FiCheck style={{ color: 'var(--success-color)', fontSize: '1.5rem' }} /> : <FiX style={{ color: 'var(--danger-color)', fontSize: '1.5rem' }} />}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="benefits">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Why Choose LogCrest Over New Relic?</h2>
            <p>Discover the advantages of LogCrest</p>
          </motion.div>
          
          <div className="features-grid">
            <motion.div 
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="feature-icon">
                <FiDollarSign />
              </div>
              <h3>Better Pricing</h3>
              <p>More cost-effective than New Relic with transparent pricing.</p>
            </motion.div>
            <motion.div 
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="feature-icon">
                <FiZap />
              </div>
              <h3>Unified Platform</h3>
              <p>Complete observability with APM, logs, and infrastructure in one platform.</p>
            </motion.div>
            <motion.div 
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="feature-icon">
                <FiShield />
              </div>
              <h3>AI-Powered APM</h3>
              <p>Advanced AI analytics for better application performance insights.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Switch from New Relic?</h2>
            <p>Experience the best New Relic alternative with unified observability and AI-powered APM.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
                <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LogCrestVsNewRelic;

